import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Briefcase, Headphones, LogIn, Mail, MapPin, Phone, ShieldCheck, ShoppingBag, Sparkles, UserPlus, Users } from "lucide-react";
import { api, imgUrl } from "@/lib/api";
import { PackageCard, ComplianceNote } from "@/components/mlm/PackageCard";
import { useSettings } from "@/components/site/Footer";

const STEPS = [
  ["Create your account", "Sign in securely with your email or Google account."],
  ["Register as distributor", "Fill in your details, verify your email and upload your ID proof and photo."],
  ["Admin verification", "Our team checks your documents and approves your account."],
  ["Get your Distributor ID", "Receive your unique ID and personal referral link in your dashboard."],
];

const REFERRAL = [
  [UserPlus, "Share your referral link", "Your link carries your Distributor ID so new members and customers are linked to you."],
  [Users, "Sponsor is recorded permanently", "When someone registers with your ID, you become their sponsor in the team network tree."],
  [ShoppingBag, "Genuine product sales", "Sales of real BMI SP GROUP products through your link are tracked as personal sales; your team's sales are tracked too."],
  [ShieldCheck, "Commission only on verified sales", "Commission rules are set by the company and apply only to verified, paid product sales."],
];

const Section = ({ eyebrow, title, sub, children, className = "", id }) => (
  <section id={id} className={`py-20 ${className}`}>
    <div className="container-x">
      <p className="text-xs font-bold uppercase tracking-widest text-amber-600">{eyebrow}</p>
      <h2 className="mt-2 text-3xl font-bold tracking-tight sm:text-4xl">{title}</h2>
      {sub && <p className="mt-3 max-w-2xl opacity-75">{sub}</p>}
      <div className="mt-10">{children}</div>
    </div>
  </section>
);

export default function Home() {
  const [pkgs, setPkgs] = useState([]);
  const [cats, setCats] = useState([]);
  const [jobs, setJobs] = useState({ jobs: [], total_vacancies: 0 });
  const s = useSettings();
  useEffect(() => {
    api.get("/mlm/packages").then((r) => setPkgs(r.data));
    api.get("/categories").then((r) => setCats(r.data));
    api.get("/jobs").then((r) => setJobs(r.data));
  }, []);

  return (
    <div>
      <section className="bg-slate-950" data-testid="home-banner">
        <img src="/images/home-banner.jpg" alt="BMI SP GROUP — Clean Home, Healthy Life. SP Quick Wash detergent powder, floor cleaner, hand wash, dish wash and toilet cleaner" className="mx-auto block h-auto w-full max-w-[1672px]" data-testid="home-banner-image" />
      </section>

      <section className="grain relative overflow-hidden bg-slate-950 text-white">
        <div className="container-x relative grid gap-10 py-16 lg:grid-cols-12 lg:py-20">
          <div className="fade-up space-y-6 lg:col-span-7">
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-400">BMI SP GROUP · Distributor Network</p>
            <h1 className="text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl" data-testid="hero-title">
              Welcome to <span className="text-amber-400">BMI SP GROUP</span>
            </h1>
            <p className="max-w-xl text-base leading-relaxed text-slate-300 md:text-lg">
              Become a registered distributor of genuine SP Quick Wash household cleaning products. Joining is free — choose an optional product package, share your referral link and grow your product business with full transparency.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/distributor/register" className="inline-flex items-center gap-2 rounded-full bg-amber-600 px-6 py-3 font-semibold transition-colors hover:bg-amber-500" data-testid="hero-register-button"><UserPlus className="h-4 w-4" /> Distributor Registration</Link>
              <Link to="/member/login" className="inline-flex items-center gap-2 rounded-full border border-white/30 px-6 py-3 font-semibold transition-colors hover:bg-white hover:text-slate-900" data-testid="hero-member-login-button"><LogIn className="h-4 w-4" /> Member Login</Link>
            </div>
          </div>
          <div className="fade-up grid content-end gap-3 lg:col-span-5" style={{ animationDelay: "150ms" }}>
            <Link to="/products" className="flex items-center justify-between rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur-lg transition-colors hover:bg-white/10" data-testid="hero-shop-button">
              <span><span className="block text-xs font-bold uppercase tracking-widest text-slate-400">Customers</span><span className="font-semibold">Buy products without joining</span></span><ShoppingBag className="h-5 w-5 text-amber-400" />
            </Link>
            <Link to="/careers" className="flex items-center justify-between rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur-lg transition-colors hover:bg-white/10" data-testid="hero-careers-button">
              <span><span className="block text-xs font-bold uppercase tracking-widest text-slate-400">Careers</span><span className="font-semibold">{jobs.total_vacancies.toLocaleString("en-IN")} job vacancies open</span></span><Briefcase className="h-5 w-5 text-amber-400" />
            </Link>
          </div>
        </div>
      </section>

      <Section id="packages" eyebrow="Distributor packages" title="Seven product packages" sub="Every package contains genuine BMI SP GROUP products. Packages are ordered only by approved distributors from their Distributor Dashboard — registration is free and no purchase is required.">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="home-packages">{pkgs.map((p, i) => <PackageCard key={p.id} pkg={p} index={i} />)}</div>
        <ComplianceNote className="mt-8" />
      </Section>

      <Section eyebrow="Product catalogue" title="SP Quick Wash household range" sub="Anyone can buy our products online — no distributor registration needed." className="bg-white">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
          {cats.map((c) => (
            <Link key={c.id} to={`/products?category=${c.id}`} className="group overflow-hidden rounded-2xl border border-slate-200 bg-slate-50" data-testid={`home-category-${c.slug}`}>
              <div className="aspect-square overflow-hidden"><img src={imgUrl(c.image)} alt={c.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /></div>
              <p className="flex items-center justify-between p-4 text-sm font-semibold">{c.name} <Sparkles className="h-4 w-4 text-amber-600" /></p>
            </Link>
          ))}
        </div>
        <Link to="/products" className="mt-6 inline-flex items-center gap-1 text-sm font-semibold hover:text-amber-700" data-testid="home-all-products">View full catalogue <ArrowRight className="h-4 w-4" /></Link>
      </Section>

      <Section eyebrow="How to join" title="Four simple steps">
        <ol className="grid gap-4 md:grid-cols-4" data-testid="how-to-join">
          {STEPS.map(([t, d], i) => (
            <li key={t} className="rounded-2xl border border-slate-200 bg-white p-6">
              <span className="font-mono-num text-3xl font-extrabold text-amber-600">0{i + 1}</span>
              <h3 className="mt-3 font-display font-bold">{t}</h3>
              <p className="mt-1 text-sm text-slate-600">{d}</p>
            </li>
          ))}
        </ol>
      </Section>

      <Section eyebrow="Referral system" title="How the referral system works" className="bg-slate-950 text-white">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4" data-testid="how-referral-works">
          {REFERRAL.map(([Icon, t, d]) => (
            <div key={t} className="rounded-2xl border border-white/10 bg-white/5 p-6">
              <Icon className="h-6 w-6 text-amber-400" />
              <h3 className="mt-4 font-display font-bold">{t}</h3>
              <p className="mt-1 text-sm text-slate-300">{d}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section eyebrow="About the business" title="A product company first" className="bg-white">
        <div className="grid gap-8 lg:grid-cols-2">
          <p className="leading-relaxed text-slate-600">BMI SP GROUP manufactures and sells SP Quick Wash detergent powder, phenyl / floor cleaner, hand wash, dish wash and toilet cleaner. Our distributor network helps genuine products reach more homes. All payments are processed securely online and every order receives an invoice.</p>
          <div className="flex flex-wrap gap-3">
            <Link to="/distributor/register" className="inline-flex items-center gap-2 rounded-full bg-amber-600 px-6 py-3 font-semibold text-white hover:bg-amber-700" data-testid="business-register-button"><UserPlus className="h-4 w-4" /> Register now</Link>
            <Link to="/packages" className="inline-flex items-center gap-2 rounded-full border border-slate-900 px-6 py-3 font-semibold hover:bg-slate-900 hover:text-white" data-testid="business-packages-button">See all packages</Link>
          </div>
        </div>
      </Section>

      <Section eyebrow="Contact & support" title="We're here to help">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="home-contact">
          {[[MapPin, "Address", s.address], [Phone, "Phone", s.phone], [Mail, "Email", s.email], [Headphones, "Member support", "Raise a ticket from your Member Dashboard"]].map(([Icon, l, v]) => (
            <div key={l} className="rounded-2xl border border-slate-200 bg-white p-6"><Icon className="mb-3 h-5 w-5 text-amber-600" /><p className="text-xs font-bold uppercase tracking-widest text-slate-500">{l}</p><p className="mt-1 font-semibold">{v || "—"}</p></div>
          ))}
        </div>
      </Section>
    </div>
  );
}
